import { useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import {
  Calendar,
  Landmark,
  Wallet,
  HandCoins,
  TrendingUp,
  Users,
  RefreshCw,
  Coins,
} from "lucide-react";
import {
  apuracaoMensalFundosService,
  movimentoFundoReservaService,
} from "../../services/apiService";
import { formatCurrencyMoeda } from "../../utils/FormatarValores";
import { useNotification } from "../../hooks/useNotification";
import { useQuery } from "../../hooks/useQuery";
import ConfirmDialog from "../../components/ConfirmDialog";
import PageContainer from "../../components/PageContainer";
import { formatarData } from "../../utils/FormatarData";
import {
  getCompetenciaAtual,
  competenciaParaInputMes,
  normalizarCompetencia,
} from "../../utils/FormatarCompetencia";

const CORES = [
  "#0284c7",
  "#16a34a",
  "#d97706",
  "#dc2626",
  "#7c3aed",
  "#0891b2",
  "#4d7c0f",
  "#be123c",
];

const formatarMetaFundo = (fundo, tipo) => {
  const metaPercentual = Number(
    fundo?.percentualMeta ??
      fundo?.percentual ??
      fundo?.porcentagem ??
      fundo?.valorPercentual ??
      fundo?.valorAporte ??
      0
  );

  const metaFixa = Number(
    fundo?.metaFixa ??
      fundo?.valorMeta ??
      fundo?.valorFixoMeta ??
      fundo?.valorAporte ??
      0
  );

  if (tipo === "Porcentagem") {
    return `${metaPercentual.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}% da meta`;
  }

  return formatCurrencyMoeda(metaFixa);
};

const normalizarValorCard = (valor) => {
  const numero = Number(valor || 0);
  return numero < 0 ? 0 : numero;
};

const ApuracaoMensalFundos = () => {
  const { showError, showSuccess } = useNotification();
  const [competencia, setCompetencia] = useState(getCompetenciaAtual());
  const [isCalculando, setIsCalculando] = useState(false);
  const [showConfirmApurar, setShowConfirmApurar] = useState(false);

  const {
    data: dados,
    isLoading,
    refetch,
  } = useQuery(
    ["apuracao-mensal-fundos", competencia],
    () => apuracaoMensalFundosService.apurarPorCompetencia(competencia),
    {
      enabled: Boolean(competencia),
      onError: (err) => {
        showError(err.message || "Erro ao carregar apuração mensal de fundos");
      },
    }
  );

  const { data: dadosExtrato } = useQuery(
    ["extrato-fundo-reserva", competencia],
    () => movimentoFundoReservaService.listarExtratoPorCompetencia(competencia),
    {
      enabled: Boolean(competencia),
      onError: (err) => {
        showError(err.message || "Erro ao carregar extrato de movimentações");
      },
    }
  );

  const dadosPizzaContas = useMemo(() => {
    const contas = Array.isArray(dados?.contas) ? dados.contas : [];
    return contas.map((conta) => ({
      name: conta.nome,
      value: Number(conta.valor || 0),
    }));
  }, [dados]);

  const dadosBarFundos = useMemo(() => {
    const fundosPct = Array.isArray(dados?.fundosPorcentagem)
      ? dados.fundosPorcentagem
      : [];
    const fundosFixo = Array.isArray(dados?.fundosFixo) ? dados.fundosFixo : [];

    return [
      ...fundosPct.map((fundo) => ({
        nome: fundo.nome,
        valor: Number(fundo.valor || 0),
        tipo: "Porcentagem",
      })),
      ...fundosFixo.map((fundo) => ({
        nome: fundo.nome,
        valor: Number(fundo.valor || 0),
        tipo: "Valor Fixo",
      })),
    ];
  }, [dados]);

  const dadosTabelaFundos = useMemo(() => {
    const fundosPct = Array.isArray(dados?.fundosPorcentagem)
      ? dados.fundosPorcentagem
      : [];
    const fundosFixo = Array.isArray(dados?.fundosFixo) ? dados.fundosFixo : [];

    return [
      ...fundosPct.map((fundo) => ({
        fundoId: fundo.fundoId,
        nome: fundo.nome,
        tipo: "Porcentagem",
        meta: formatarMetaFundo(fundo, "Porcentagem"),
        valor: Number(fundo.valor || 0),
      })),
      ...fundosFixo.map((fundo) => ({
        fundoId: fundo.fundoId,
        nome: fundo.nome,
        tipo: "Valor Fixo",
        meta: formatarMetaFundo(fundo, "Valor Fixo"),
        valor: Number(fundo.valor || 0),
      })),
    ];
  }, [dados]);

  const totalValorFundosGrafico = useMemo(() => {
    return dadosBarFundos.reduce((acc, item) => acc + Number(item.valor || 0), 0);
  }, [dadosBarFundos]);

  const dadosTabelaExtrato = useMemo(() => {
    const fundos = Array.isArray(dadosExtrato) ? dadosExtrato : [];

    return fundos.flatMap((fundo, fundoIndex) => {
      const movimentos = Array.isArray(fundo?.movimentos) ? fundo.movimentos : [];

      return movimentos.map((movimento, movimentoIndex) => ({
        id:
          movimento?.id ??
          `${fundo?.fundoId || "fundo"}-${fundoIndex}-${movimentoIndex}`,
        fundoNome: fundo?.nomeFundo || "Fundo de reserva",
        descricao: movimento?.observacao || "-",
        competencia,
        dataMovimento:
          movimento?.dataReferenciaMes || movimento?.dataMovimento || null,
        valorMovimento: Number(movimento?.valorMovimento || 0),
      }));
    });
  }, [dadosExtrato, competencia]);

  const apuracaoNaoCalculada = useMemo(() => {
    if (!dados) {
      return false;
    }

    const totalContas = Number(dados.totalContas || 0);
    const valorTotalFundos = Number(dados.valorTotalFundos || 0);
    const contas = Array.isArray(dados.contas) ? dados.contas : [];
    const fundosPorcentagem = Array.isArray(dados.fundosPorcentagem)
      ? dados.fundosPorcentagem
      : [];
    const fundosFixo = Array.isArray(dados.fundosFixo) ? dados.fundosFixo : [];

    return (
      totalContas === 0 &&
      valorTotalFundos === 0 &&
      contas.length === 0 &&
      fundosPorcentagem.length === 0 &&
      fundosFixo.length === 0
    );
  }, [dados]);

  const handleCompetenciaChange = (e) => {
    const value = normalizarCompetencia(e.target.value);
    if (!value) {
      return;
    }
    setCompetencia(value);
  };

  const handleApurar = async () => {
    setIsCalculando(true);

    try {
      await apuracaoMensalFundosService.calcularPorCompetencia(competencia);
      showSuccess("Apuração realizada com sucesso");
      setShowConfirmApurar(false);
      await refetch();
    } catch (err) {
      showError(err.message || "Erro ao apurar fundos da competência");
    } finally {
      setIsCalculando(false);
    }
  };

  return (
    <PageContainer className="p-4 md:p-6 space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Apuracao Mensal de Fundos
          </h1>
          <p className="text-gray-600">
            Consolidado financeiro por competencia com contas, fundos e sobra
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Competência
            </label>
            <input
              type="month"
              value={competenciaParaInputMes(competencia)}
              onChange={handleCompetenciaChange}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <button
            onClick={() => setShowConfirmApurar(true)}
            disabled={isLoading || isCalculando}
            className="h-fit self-end bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <RefreshCw
              size={16}
              className={isCalculando ? "animate-spin" : ""}
            />
            {isCalculando ? "Apurando..." : "Apurar"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow border p-4">
          <p className="text-sm text-gray-500">Total das Contas</p>
          <p className="text-2xl font-bold text-black">
            {formatCurrencyMoeda(normalizarValorCard(dados?.totalContas))}
          </p>
          <div className="mt-2 text-blue-600">
            <Landmark size={18} />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow border p-4">
          <p className="text-sm text-gray-500">Total dos Fundos</p>
          <p className="text-2xl font-bold text-black">
            {formatCurrencyMoeda(normalizarValorCard(dados?.valorTotalFundos))}
          </p>
          <div className="mt-2 text-red-600">
            <Wallet size={18} />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow border p-4">
          <p className="text-sm text-gray-500">Sobra</p>
          <p className="text-2xl font-bold text-black">
            {formatCurrencyMoeda(normalizarValorCard(dados?.sobra))}
          </p>
          <div className="mt-2 text-emerald-600">
            <TrendingUp size={18} />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow border p-4">
          <p className="text-sm text-gray-500">Divisao de Lucro</p>
          <p className="text-2xl font-bold text-black">
            {formatCurrencyMoeda(normalizarValorCard(dados?.divisaoLucro))}
          </p>
          <div className="mt-2 text-purple-600 flex items-center gap-2">
            <Users size={18} />
            <span className="text-sm text-black">
              {dados?.numeroSocios || 0} sócios
            </span>
          </div>
        </div>

        {Number(dados?.sobraDivisaoLucro) > 0 && (
          <div className="bg-white rounded-lg shadow border p-4">
            <p className="text-sm text-gray-500">Sobra Divisao de Lucro</p>
            <p className="text-2xl font-bold text-black">
              {formatCurrencyMoeda(normalizarValorCard(dados?.sobraDivisaoLucro))}
            </p>
            <div className="mt-2 text-amber-600">
              <Coins size={18} />
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow border p-4">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Calendar size={18} />
            Distribuicao por Conta
          </h2>
          {isLoading ? (
            <div className="h-[320px] flex items-center justify-center text-gray-500">
              Carregando dados...
            </div>
          ) : dadosPizzaContas.length === 0 ? (
            <div className="h-[320px] flex items-center justify-center text-gray-500">
              {apuracaoNaoCalculada
                ? "Clique em Apurar para gerar os dados do gráfico"
                : "Nenhuma conta encontrada para a competencia selecionada"}
            </div>
          ) : (
            <div className="w-full h-[320px]">
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={dadosPizzaContas}
                    dataKey="value"
                    nameKey="name"
                    outerRadius={105}
                    label={({ value }) => formatCurrencyMoeda(value)}
                  >
                    {dadosPizzaContas.map((item, index) => (
                      <Cell
                        key={`${item.name}-${index}`}
                        fill={CORES[index % CORES.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => [formatCurrencyMoeda(value), "Valor"]}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow border p-4">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <HandCoins size={18} />
            Fundos por Tipo
          </h2>
          {isLoading ? (
            <div className="h-[320px] flex items-center justify-center text-gray-500">
              Carregando dados...
            </div>
          ) : dadosBarFundos.length === 0 ? (
            <div className="h-[320px] flex items-center justify-center text-gray-500">
              {apuracaoNaoCalculada
                ? "Clique em Apurar para gerar os dados do gráfico"
                : "Nenhum fundo encontrado para a competencia selecionada"}
            </div>
          ) : (
            <div className="w-full h-[320px]">
              <ResponsiveContainer>
                <BarChart
                  data={dadosBarFundos}
                  margin={{ top: 20, right: 10, left: 0, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="nome"
                    interval={0}
                    angle={-20}
                    textAnchor="end"
                    height={70}
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis
                    tickFormatter={(value) =>
                      new Intl.NumberFormat("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                        maximumFractionDigits: 0,
                      }).format(value)
                    }
                  />
                  <Tooltip
                    formatter={(value) => [formatCurrencyMoeda(value), "Valor"]}
                  />
                  <Legend />
                  <Bar
                    dataKey="valor"
                    name={`Valor Total dos Fundos de Reserva (${formatCurrencyMoeda(totalValorFundosGrafico)})`}
                    fill="#2563eb"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow border p-4">
        <h2 className="text-lg font-semibold mb-4">Contas do Mês</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="p-2 text-left">Conta</th>
                <th className="p-2 text-right">Valor</th>
              </tr>
            </thead>
            <tbody>
              {(dados?.contas || []).map((conta) => (
                <tr key={conta.contaId} className="border-t">
                  <td className="p-2">{conta.nome}</td>
                  <td className="p-2 text-right font-semibold">
                    {formatCurrencyMoeda(conta.valor)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow border p-4 space-y-6">
        <div>
          <h2 className="text-lg font-semibold mb-4">Fundos Consolidados</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-2 text-left">Fundo</th>
                  <th className="p-2 text-left">Tipo</th>
                  <th className="p-2 text-right">Valor</th>
                </tr>
              </thead>
              <tbody>
                {dadosTabelaFundos.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="p-4 text-center text-gray-500">
                      {apuracaoNaoCalculada
                        ? "Clique em Apurar para gerar os fundos"
                        : "Nenhum fundo encontrado para a competencia selecionada"}
                    </td>
                  </tr>
                ) : (
                  dadosTabelaFundos.map((fundo, index) => (
                    <tr
                      key={`${fundo.fundoId || fundo.nome}-${index}`}
                      className="border-t"
                    >
                      <td className="p-2">{fundo.nome}</td>
                      <td className="p-2">{fundo.tipo}</td>
                      <td className="p-2 text-right font-semibold">
                        {formatCurrencyMoeda(fundo.valor)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="pt-2 border-t">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <tbody>
                <tr>
                  <td className="p-2">Valor para Aplicação</td>
                  <td className="p-2 text-right font-semibold text-base">
                    {formatCurrencyMoeda(
                      normalizarValorCard(dados?.valorAplicacaoInvestimento)
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow border p-4">
        <h2 className="text-lg font-semibold mb-4">Extrato de Movimentações dos Fundos de Reserva</h2>

        {isLoading ? (
          <div className="py-8 text-center text-gray-500">Carregando extrato...</div>
        ) : dadosTabelaExtrato.length === 0 ? (
          <div className="py-8 text-center text-gray-500">
            {apuracaoNaoCalculada
              ? "Clique em Apurar para gerar o extrato"
              : "Nenhuma movimentação encontrada no extrato para a competência selecionada"}
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {dadosTabelaExtrato.map((item) => {
              const isEntrada = Number(item.valorMovimento) >= 0;

              return (
                <div
                  key={item.id}
                  className="py-3 flex items-center justify-between gap-4"
                >
                  <div className="min-w-0">
                    <p className="text-base font-semibold text-gray-900 truncate">
                      {item.fundoNome}
                    </p>
                    <p className="text-sm text-black truncate">
                      {item.descricao}
                    </p>
                    <p className="text-sm text-black">
                      {formatarData(item.dataMovimento)}
                    </p>
                  </div>

                  <div
                    className={`text-lg font-semibold whitespace-nowrap ${
                      isEntrada ? "text-emerald-600" : "text-red-500"
                    }`}
                  >
                    <span>{formatCurrencyMoeda(Math.abs(item.valorMovimento))}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={showConfirmApurar}
        title="Confirmar apuração"
        description={
          dados?.lancamentoAutomatico
            ? "Já foi realizado apuração desse mês, deseja recalcular?"
            : `Deseja apurar os fundos da competência ${competencia}?`
        }
        confirmText="Confirmar"
        cancelText="Cancelar"
        isLoading={isCalculando}
        onCancel={() => setShowConfirmApurar(false)}
        onConfirm={handleApurar}
      />
    </PageContainer>
  );
};

export default ApuracaoMensalFundos;
